import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  PaperPlaneRight, 
  ShieldCheck,
  FirstAidKit,
  Heart,
  Handshake
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getOrCreateAnonymousSessionId } from '@/utils/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const quickReplyOptions = {
  party_smart: [
    'Checklist',
    'Pack list',
    'Mixing'
  ],
  risk_situation: [
    'PEP',
    'Testing',
    'Find a clinic'
  ],
  emotional_support: [
    'Ground me',
    'I feel ashamed',
    'I need aftercare'
  ],
  harm_reduction: [
    'Short version',
    'Tea2Go info',
    'What should I avoid mixing?'
  ],
  resource_food: [
    'Meals',
    'Groceries',
    'Open resources'
  ],
  resource_clinic: [
    'PEP',
    'Testing',
    'Clinic',
    'Open resources'
  ],
  resource_legal: [
    'Legal aid',
    'Rights',
    'Open resources'
  ],
  resource_counselling: [
    'Counselling',
    'Crisis line',
    'Open resources'
  ],
  resource_housing: [
    'Housing',
    'Shelter',
    'Open resources'
  ],
  resource_nav: [
    'Open resources'
  ],
  default: [
    'Keep it short',
    'Tell me more',
    'Find support near me'
  ]
};

const inferConversationMode = (text) => {
  const lowerText = text.toLowerCase();
  if (['food', 'food bank', 'meal', 'grocer', 'snack'].some((term) => lowerText.includes(term))) {
    return 'resource_food';
  }
  if (['pep', 'prep', 'testing', 'clinic', 'sti', 'hiv test'].some((term) => lowerText.includes(term))) {
    return 'resource_clinic';
  }
  if (['legal', 'lawyer', 'rights', 'immigration', 'tenant'].some((term) => lowerText.includes(term))) {
    return 'resource_legal';
  }
  if (['counselling', 'counseling', 'therapy', 'mental health', 'support service'].some((term) => lowerText.includes(term))) {
    return 'resource_counselling';
  }
  if (['housing', 'shelter', 'homeless', 'rent'].some((term) => lowerText.includes(term))) {
    return 'resource_housing';
  }
  if (['resource', 'support', 'service'].some((term) => lowerText.includes(term))) {
    return 'resource_nav';
  }
  if (['exposed', 'pep', 'test'].some((term) => lowerText.includes(term))) {
    return 'risk_situation';
  }
  if (['alone', 'low', 'sad', 'down', 'spiral'].some((term) => lowerText.includes(term))) {
    return 'emotional_support';
  }
  return 'default';
};

const ChatPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => getOrCreateAnonymousSessionId());
  const [activeMode, setActiveMode] = useState('default');
  const [responseStyle, setResponseStyle] = useState('short');
  const [hasSelectedCity, setHasSelectedCity] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickActions = [
    { id: 'party_smart', icon: ShieldCheck, label: 'Party Smart', caption: 'I’m planning to party' },
    { id: 'risk_situation', icon: FirstAidKit, label: 'Risk Situation', caption: 'I think I was exposed' },
    { id: 'emotional_support', icon: Heart, label: 'Emotional Support', caption: 'I feel alone' },
    { id: 'harm_reduction', icon: Handshake, label: 'Harm Reduction Kit', caption: 'Condoms, lube, water, snacks' }
  ];

  useEffect(() => {
    const action = searchParams.get('action');
    if (action && messages.length === 0) {
      handleQuickAction(action);
    }
  }, [searchParams]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadChatHistory();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history/${sessionId}`);
      if (response.data.messages && response.data.messages.length > 0) {
        const formattedMessages = response.data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async (message, quickAction = null, forcedMode = null) => {
    if (!message.trim() && !quickAction) return;

    if (!quickAction && message.trim()) {
      setActiveMode(forcedMode || inferConversationMode(message));
    }

    const userMessage = { role: 'user', content: message, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: message,
        quick_action: quickAction,
        response_style: responseStyle
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.assistant_message,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue);
    }
  };

  const handleQuickAction = (actionId) => {
    setActiveMode(actionId);
    sendMessage('', actionId);
  };

  const handleQuickReply = (replyText) => {
    if (replyText === 'Open resources') {
      navigate('/resources');
      return;
    }
    // Track if user selected a city
    if (['Ottawa', 'Toronto', 'Online'].includes(replyText)) {
      setHasSelectedCity(true);
    }
    const keepMode = activeMode.startsWith('resource_') && ['Ottawa', 'Toronto', 'Online'].includes(replyText)
      ? activeMode
      : null;
    sendMessage(replyText, null, keepMode);
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-to-home-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[rgb(var(--primary))] to-[rgb(var(--accent))] p-0.5 mr-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_nad-peer-support/artifacts/7ysat3zd_1053.jpg"
                  alt="Nad"
                  className="w-full h-full rounded-full object-cover"
                  data-testid="chat-header-avatar"
                />
              </div>
              <div>
                <h1 className="text-lg font-semibold tracking-tight text-[rgb(var(--text-primary))]" data-testid="chat-header-title">
                  Nad
                </h1>
                <p className="text-xs text-[rgb(var(--text-secondary))]" data-testid="chat-header-subtitle">
                  Peer Support Worker
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Messages Area */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-6 pb-32 overflow-y-auto" data-testid="chat-messages-container">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[rgb(var(--primary))] to-[rgb(var(--accent))] p-1 mx-auto mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_nad-peer-support/artifacts/7ysat3zd_1053.jpg"
                  alt="Nad"
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              <h2 className="text-2xl font-semibold text-[rgb(var(--text-primary))] mb-2">
                Hey! I'm Nad
              </h2>
              <p className="text-[rgb(var(--text-secondary))] max-w-md leading-relaxed">
                How can I support you today? Pick a mode below or just start typing.
              </p>
              <p className="mt-3 text-xs text-[rgb(var(--text-secondary))]" data-testid="chat-safety-note">
                This is peer support, not medical advice.
              </p>
              <div className="mt-4 inline-flex rounded-full border border-white/10 bg-[rgb(var(--surface))]/80 p-1" data-testid="chat-response-style-toggle">
                <button
                  type="button"
                  onClick={() => setResponseStyle('short')}
                  data-testid="chat-style-short-button"
                  className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                    responseStyle === 'short' ? 'bg-[rgb(var(--primary))] text-white' : 'text-[rgb(var(--text-secondary))]'
                  }`}
                >
                  Short answers
                </button>
                <button
                  type="button"
                  onClick={() => setResponseStyle('detail')}
                  data-testid="chat-style-detail-button"
                  className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                    responseStyle === 'detail' ? 'bg-[rgb(var(--primary))] text-white' : 'text-[rgb(var(--text-secondary))]'
                  }`}
                >
                  More detail
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-md">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action.id)}
                    data-testid={`chat-quick-action-${action.id}`}
                    disabled={isLoading}
                    className="flex flex-col items-center justify-center p-4 bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl border border-white/5 transition-all duration-200 hover:border-[rgb(var(--primary))]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] mb-2" />
                    <span className="text-sm text-[rgb(var(--text-primary))] font-medium">
                      {action.label}
                    </span>
                    <span className="mt-1 text-xs text-[rgb(var(--text-secondary))] text-center leading-relaxed">
                      {action.caption}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                data-testid={`chat-message-${message.role}-${index}`}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} message-enter message-enter-active`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[rgb(var(--primary))] to-[rgb(var(--accent))] p-0.5 mr-2 flex-shrink-0">
                    <img
                      src="https://customer-assets.emergentagent.com/job_nad-peer-support/artifacts/7ysat3zd_1053.jpg"
                      alt="Nad"
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                )}
                <div
                  className={`max-w-[75%] p-4 rounded-2xl ${
                    message.role === 'user'
                      ? 'rounded-br-sm bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 text-[rgb(var(--text-primary))]'
                      : 'rounded-bl-sm bg-[rgb(var(--surface-elevated))] border border-white/5 text-[rgb(var(--text-primary))]'
                  }`}
                >
                  <p className="leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  {message.role === 'assistant' && index === messages.length - 1 && (
                    <div className="mt-4">
                      {/* Show city chips + service chips together for resource modes */}
                      {activeMode.startsWith('resource_') && !hasSelectedCity && (
                        <div className="mb-3 p-3 rounded-xl bg-[rgb(var(--primary))]/5 border border-[rgb(var(--primary))]/20" data-testid="chat-city-service-combo">
                          <p className="text-xs text-[rgb(var(--text-secondary))] mb-2">Where are you located? 📍</p>
                          <div className="flex flex-wrap gap-2 mb-3" data-testid="chat-resource-city-chips">
                            {['Ottawa', 'Toronto', 'Online'].map((city) => (
                              <button
                                key={city}
                                type="button"
                                onClick={() => handleQuickReply(city)}
                                data-testid={`chat-city-chip-${city.toLowerCase()}`}
                                className="rounded-full border border-[rgb(var(--primary))]/40 bg-[rgb(var(--primary))]/15 px-4 py-2 text-sm font-medium text-[rgb(var(--primary))] transition-colors hover:bg-[rgb(var(--primary))]/25"
                              >
                                {city}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-[rgb(var(--text-secondary))] mb-2">Or what do you need? 💜</p>
                          <div className="flex flex-wrap gap-2" data-testid="chat-service-chips">
                            {(quickReplyOptions[activeMode] || quickReplyOptions.default).map((reply) => (
                              <button
                                key={reply}
                                type="button"
                                onClick={() => handleQuickReply(reply)}
                                data-testid={`chat-service-chip-${reply.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                                className="rounded-full border border-white/10 bg-black/10 px-3 py-2 text-xs text-[rgb(var(--text-secondary))] transition-colors hover:border-[rgb(var(--primary))]/30 hover:text-[rgb(var(--text-primary))]"
                              >
                                {reply}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Show only service chips after city selected or for non-resource modes */}
                      {(!activeMode.startsWith('resource_') || hasSelectedCity) && (
                        <div className="flex flex-wrap gap-2" data-testid="chat-quick-reply-chips">
                          {(quickReplyOptions[activeMode] || quickReplyOptions.default).map((reply) => (
                            <button
                              key={reply}
                              type="button"
                              onClick={() => handleQuickReply(reply)}
                              data-testid={`chat-quick-reply-${reply.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                              className="rounded-full border border-white/10 bg-black/10 px-3 py-2 text-xs text-[rgb(var(--text-secondary))] transition-colors hover:border-[rgb(var(--primary))]/30 hover:text-[rgb(var(--text-primary))]"
                            >
                              {reply}
                            </button>
                          ))}
                        </div>
                      )}
                      {activeMode.startsWith('resource_') && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => navigate('/resources')}
                          data-testid="chat-open-community-resources-button"
                          className="mt-3 rounded-full border-[rgb(var(--primary))]/30 text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10"
                        >
                          Open Community Resources
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start" data-testid="typing-indicator">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[rgb(var(--primary))] to-[rgb(var(--accent))] p-0.5 mr-2 flex-shrink-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_nad-peer-support/artifacts/7ysat3zd_1053.jpg"
                    alt="Nad"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <div className="bg-[rgb(var(--surface-elevated))] border border-white/5 rounded-2xl rounded-bl-sm p-4">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-[rgb(var(--primary))] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-[rgb(var(--primary))] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-[rgb(var(--primary))] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <div className="max-w-2xl mx-auto px-6 pb-6">
          <form onSubmit={handleSubmit} className="glass-morphism rounded-full p-2 border">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                disabled={isLoading}
                data-testid="chat-input"
                className="flex-1 bg-transparent border-0 outline-none text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-secondary))] px-4 py-2 disabled:opacity-50"
              />
              <Button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                data-testid="send-message-button"
                size="icon"
                className="rounded-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/90 text-white h-10 w-10 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperPlaneRight size={20} weight="fill" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;